// =========================================================
// * Vue Material Dashboard - v1.4.0
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
// * Licensed under MIT (https://github.com/creativetimofficial/vue-material-dashboard/blob/master/LICENSE.md)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp } from "vue";
import { createStore } from "vuex";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App";
import { ISI_API_URL } from "./config";

// router setup
import routes from "./routes/routes";

// Plugins
import GlobalComponents from "./globalComponents";
import GlobalDirectives from "./globalDirectives";
import Notifications from "./components/NotificationPlugin";
// import SidebarPlugin from "./components/SidebarPlugin";
import axios from "axios";

// Automation auth: capture ?auth_key= from URL into localStorage, then inject
// as Authorization header on every request. Enables headless-browser testing.
const urlParams = new URLSearchParams(window.location.search);
const authKeyFromUrl = urlParams.get("auth_key");
if (authKeyFromUrl) {
  localStorage.setItem("admin_auth_key", authKeyFromUrl);
}
axios.interceptors.request.use((config) => {
  const authKey = localStorage.getItem("admin_auth_key");
  if (authKey) {
    config.headers.Authorization = `Bearer ${authKey}`;
  }
  return config;
});

// Vuetify plugin
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import "./assets/css/id-mono.css";

import * as Chartist from "chartist";

// configure router
const router = createRouter({
  history: createWebHistory(),
  routes,
  linkExactActiveClass: "nav-item active",
});

const store = createStore({
  state: {
    user_data: { email: "", is_admin: false, isi_token: null },
    selected_user: { email: "" },
    user_list: [],
    authInitialized: false,
  },
  getters: {},
  mutations: {
    update_user_list(state, new_data) {
      state.user_list = new_data;
    },
    update_selected_user(state, new_data) {
      state.selected_user = new_data;
    },
    update_user_data(state, new_data) {
      state.user_data = new_data;
    },
    update_purchases(state, new_data) {
      state.purchases = new_data;
    },
    update_products(state, new_data) {
      state.products = new_data;
    },
    set_auth_initialized(state, value) {
      state.authInitialized = value;
    },
  },
  actions: {},
});

// Navigation guard for admin-only routes
router.beforeEach((to, from, next) => {
  if (to.meta.requiresAdmin) {
    // If auth is not initialized yet, wait for it
    if (!store.state.authInitialized) {
      // Wait for auth to complete
      const unwatch = store.watch(
        (state) => state.authInitialized,
        (authInitialized) => {
          if (authInitialized) {
            unwatch(); // Stop watching
            // Now check if user is admin
            if (store.state.user_data.is_admin) {
              next();
            } else {
              next("/dashboard");
            }
          }
        },
      );
      return; // Don't call next() yet
    }

    // Auth is already initialized, check admin status
    if (!store.state.user_data.is_admin) {
      next("/dashboard");
      return;
    }
  }
  next();
});

const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: "light",
  },
});

const app = createApp(App);

app.config.globalProperties.$Chartist = Chartist;
app.config.globalProperties.$http = axios;

app.use(router);
app.use(store);
app.use(vuetify);
app.use(GlobalComponents);
app.use(GlobalDirectives);
app.use(Notifications);
// app.use(SidebarPlugin);

window.isi_v = app.mount("#app");
